import { z } from 'zod';

export const poLineSchema = z.object({
  lineId: z.string(),
  itemId: z.string(),
  itemName: z.string(),
  sku: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitPrice: z.number(),
  totalPrice: z.number(),
  receivedQuantity: z.number().optional(),
});

export const purchaseOrderSchema = z.object({
  poId: z.string(),
  poNumber: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  status: z.enum(['draft', 'pending', 'approved', 'sent', 'acknowledged', 'shipped', 'delivered', 'cancelled']),
  deliveryZone: z.string(),
  deliverySiteId: z.string(),
  deliverySiteName: z.string(),
  expectedDeliveryDate: z.string(),
  actualDeliveryDate: z.string().optional(),
  lines: z.array(poLineSchema),
  subtotal: z.number(),
  tax: z.number(),
  total: z.number(),
  currency: z.string(),
  notes: z.string().optional(),
  createdBy: z.string(),
  createdByName: z.string(),
  approvedBy: z.string().optional(),
  approvedByName: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;
export type POLine = z.infer<typeof poLineSchema>;
export type POStatus = PurchaseOrder['status'];

export const purchaseOrdersResponseSchema = z.object({
  data: z.array(purchaseOrderSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
});

export type PurchaseOrdersResponse = z.infer<typeof purchaseOrdersResponseSchema>;

export const PO_STATUS_LABELS: Record<POStatus, string> = {
  draft: 'Draft',
  pending: 'Pending Approval',
  approved: 'Approved',
  sent: 'Awaiting Acknowledgment',
  acknowledged: 'Acknowledged',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const PO_STATUS_COLORS: Record<POStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  sent: 'bg-indigo-100 text-indigo-800',
  acknowledged: 'bg-cyan-100 text-cyan-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

// Supplier actions on POs
export type SupplierPOAction = 'acknowledge' | 'ship' | 'request_change';

// Pending Actions / Discrepancies
export const discrepancyTypeSchema = z.enum([
  'missing_items',
  'damaged_items',
  'wrong_items',
  'quantity_mismatch',
  'quality_issue',
]);

export type DiscrepancyType = z.infer<typeof discrepancyTypeSchema>;

export const discrepancyStatusSchema = z.enum([
  'open',
  'pending_supplier_response',
  'pending_receiver_confirmation',
  'resolved',
  'disputed',
]);

export type DiscrepancyStatus = z.infer<typeof discrepancyStatusSchema>;

export const resolutionTypeSchema = z.enum([
  'credit_note',
  'redelivery',
  'replacement',
  'refund',
  'no_action',
]);

export type ResolutionType = z.infer<typeof resolutionTypeSchema>;

export const discrepancyItemSchema = z.object({
  lineId: z.string(),
  itemName: z.string(),
  sku: z.string(),
  orderedQuantity: z.number(),
  receivedQuantity: z.number(),
  affectedQuantity: z.number(),
  unit: z.string(),
});

export const discrepancySchema = z.object({
  discrepancyId: z.string(),
  poId: z.string(),
  poNumber: z.string(),
  type: discrepancyTypeSchema,
  status: discrepancyStatusSchema,
  items: z.array(discrepancyItemSchema),
  description: z.string(),
  reportedBy: z.string(),
  reportedByName: z.string(),
  reportedAt: z.string(),
  proposedResolution: resolutionTypeSchema.optional(),
  finalResolution: resolutionTypeSchema.optional(),
  resolvedAt: z.string().optional(),
  resolvedBy: z.string().optional(),
  creditNoteAmount: z.number().optional(),
  currency: z.string(),
});

export type Discrepancy = z.infer<typeof discrepancySchema>;
export type DiscrepancyItem = z.infer<typeof discrepancyItemSchema>;

export const discrepanciesResponseSchema = z.object({data: z.array(discrepancySchema),});

export type DiscrepanciesResponse = z.infer<typeof discrepanciesResponseSchema>;

// Messages / Communication
export const messageAttachmentSchema = z.object({
  attachmentId: z.string(),
  fileName: z.string(),
  fileUrl: z.string(),
  fileType: z.string(),
  thumbnailUrl: z.string().optional(),
});

export type MessageAttachment = z.infer<typeof messageAttachmentSchema>;

export const messageSchema = z.object({
  messageId: z.string(),
  discrepancyId: z.string(),
  senderId: z.string(),
  senderName: z.string(),
  senderRole: z.enum(['supplier', 'receiver']),
  content: z.string(),
  attachments: z.array(messageAttachmentSchema).optional(),
  createdAt: z.string(),
});

export type Message = z.infer<typeof messageSchema>;

export const messagesResponseSchema = z.object({data: z.array(messageSchema),});

export type MessagesResponse = z.infer<typeof messagesResponseSchema>;

// Labels and colors
export const DISCREPANCY_TYPE_LABELS: Record<DiscrepancyType, string> = {
  missing_items: 'Missing Items',
  damaged_items: 'Damaged Items',
  wrong_items: 'Wrong Items',
  quantity_mismatch: 'Quantity Mismatch',
  quality_issue: 'Quality Issue',
};

export const DISCREPANCY_TYPE_COLORS: Record<DiscrepancyType, string> = {
  missing_items: 'bg-orange-100 text-orange-800',
  damaged_items: 'bg-red-100 text-red-800',
  wrong_items: 'bg-purple-100 text-purple-800',
  quantity_mismatch: 'bg-amber-100 text-amber-800',
  quality_issue: 'bg-rose-100 text-rose-800',
};

export const DISCREPANCY_STATUS_LABELS: Record<DiscrepancyStatus, string> = {
  open: 'Open',
  pending_supplier_response: 'Awaiting Your Response',
  pending_receiver_confirmation: 'Awaiting Confirmation',
  resolved: 'Resolved',
  disputed: 'Disputed',
};

export const DISCREPANCY_STATUS_COLORS: Record<DiscrepancyStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  pending_supplier_response: 'bg-amber-100 text-amber-800',
  pending_receiver_confirmation: 'bg-cyan-100 text-cyan-800',
  resolved: 'bg-green-100 text-green-800',
  disputed: 'bg-red-100 text-red-800',
};

export const RESOLUTION_TYPE_LABELS: Record<ResolutionType, string> = {
  credit_note: 'Credit Note',
  redelivery: 'Redelivery',
  replacement: 'Replacement',
  refund: 'Full Refund',
  no_action: 'No Action Required',
};
